import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_ENDPOINTS } from "../apiConfig";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const response = await axios.post(`${API_ENDPOINTS.GET_USERS}/login`, {
        username,
        password,
      });

      const { role } = response.data;
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("role", role);
      toast.success("Login successful!");
      navigate("/");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Invalid username or password";
      setLoginError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('images/DSCF8879.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-black/30"></div>
      </div>

      {/* Header Container */}
      <div className="relative z-10 w-full">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex-shrink-0">
              <img
                src="images/ambujaLogo.png"
                alt="Ambuja Foundation"
                className="w-32 md:w-40 lg:w-48 h-auto hover:scale-105 transition-transform duration-300"
              />
            </div>

            {/* Transparent Header Login Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsLoginOpen(true)}
              className="bg-transparent border-2 border-white text-white px-6 py-2.5 rounded-full shadow-lg font-semibold hover:bg-white/10 transition-all duration-300 text-base backdrop-blur-sm"
            >
              Login
            </motion.button>
          </div>
        </div>
      </div>

      {/* Welcome Text Container with Reduced Size */}
      <div className="absolute bottom-8 sm:bottom-16 md:bottom-24 left-4 sm:left-8 md:left-12 text-white z-10 max-w-[90vw] md:max-w-4xl mb-12">
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-6"
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
            "Empowering Minds, Enabling Futures: HR Initiatives for Collective Success."
          </h1>
          
          {/* Transparent Get Started Button */}
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(255,255,255,0.2)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsLoginOpen(true)}
            className="mt-8 bg-transparent border-2 border-white text-white px-8 py-3 rounded-full shadow-lg font-semibold hover:bg-white/10 transition-all duration-300 text-lg "
          >
            Get Started →
          </motion.button>
        </motion.div>
      </div>

      {/* Login Form Modal */}
      <AnimatePresence>
        {isLoginOpen && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-20 p-4 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 backdrop-blur-md bg-black/70"
              onClick={() => setIsLoginOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white/95 backdrop-blur-sm p-8 rounded-3xl shadow-2xl w-full max-w-sm sm:max-w-md relative z-30 border border-white/20"
            >
              <button
                className="absolute top-4 right-4 text-gray-600 hover:text-red-500 transition-colors text-xl"
                onClick={() => setIsLoginOpen(false)}
              >
                ✕
              </button>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col items-center mb-8"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl flex items-center justify-center mb-4 hover:scale-105 transition-transform duration-300">
                  <img
                    src="images/logo.jpg"
                    alt="Logo"
                    className="w-20 h-20 object-contain"
                  />
                </div>
                <h2 className="text-3xl font-bold text-[#1a237e]">Welcome Back</h2>
                <p className="text-gray-600 mt-2 text-base">Please sign in to your account</p>
              </motion.div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="relative group"
                >
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-800 group-hover:text-blue-500 transition-colors">
                    <FaUser size={20} className="fill-current" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors bg-white/90 hover:bg-white text-base"
                    placeholder="Username"
                    required
                  />
                </motion.div>

                <motion.div
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="relative group"
                >
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-800 group-hover:text-blue-500 transition-colors">
                    <FaLock size={20} className="fill-current" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors bg-white/90 hover:bg-white text-base"
                    placeholder="Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-800 hover:text-blue-500 transition-colors"
                  >
                    {showPassword ? (
                      <FaEyeSlash size={20} className="fill-current" />
                    ) : (
                      <FaEye size={20} className="fill-current" />
                    )}
                  </button>
                </motion.div>

                {loginError && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-500 text-sm text-center"
                  >
                    {loginError}
                  </motion.div>
                )}

                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full bg-[#1a237e] text-white py-4 rounded-xl font-semibold transition-all hover:bg-[#1a237e]/90 hover:shadow-xl text-base"
                  >
                    Sign In
                  </motion.button>
                </motion.div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;